import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Sun, Moon, UserCircle } from '@phosphor-icons/react';
import { useTheme } from '@/lib/useTheme';
import { languages } from '@/data/languages';
import { cn } from '@/lib/utils';
import { FlagEN, FlagSV, FlagNO, FlagDA, FlagFI } from '@/components/Flags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePickerPopover } from '@/components/ui/date-picker-popover';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { EmailAddresses } from '@/components/account/EmailAddresses';
import {
  getAccountProfile,
  toUpsert,
  updateAccountProfile,
  type AccountProfile,
} from '@/api/account';

const PROFILE_QUERY_KEY = ['account', 'profile'] as const;

// todayISO returns today as YYYY-MM-DD in the viewer's own timezone. Built from
// local date parts rather than toISOString(), which is UTC and would report
// tomorrow (or yesterday) either side of midnight.
function todayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const FLAG_COMPONENTS: Record<string, React.ComponentType<{ className?: string }>> = {
  en: FlagEN,
  sv: FlagSV,
  no: FlagNO,
  da: FlagDA,
  fi: FlagFI,
};

export function Profile() {
  const { t, i18n } = useTranslation('common');
  const { theme, setTheme } = useTheme();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const profile = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => getAccountProfile(token),
    enabled: auth.isAuthenticated,
  });

  // The profile POST is a full replace, not a patch — always resend the saved
  // row with just the intended field changed (see AccountProfileUpsert).
  const save = useMutation({
    mutationFn: (patch: Partial<ReturnType<typeof toUpsert>>) => {
      if (!profile.data) throw new Error('profile not loaded');
      return updateAccountProfile(token, { ...toUpsert(profile.data), ...patch });
    },
    onSuccess: (next) => queryClient.setQueryData(PROFILE_QUERY_KEY, next),
  });

  const currentLang = i18n.language;

  // Switching language changes the UI immediately AND persists to
  // core.user.language_code, so the choice survives a new browser or device and
  // server-composed email lands in the right language. Persisting is
  // best-effort: the UI switch must not wait on (or be undone by) the request.
  function pickLanguage(code: string) {
    if (code === currentLang) return;
    void i18n.changeLanguage(code);
    if (profile.data && profile.data.language_code !== code) {
      save.mutate({ language_code: code });
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.title')}
        </p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('settings.profile.title')}
        </h1>
        <p className="text-muted-foreground">{t('settings.profile.subtitle')}</p>
      </header>

      {/* Personal details card */}
      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <UserCircle size={20} weight="bold" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.profile.details.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.profile.details.body')}
            </p>
          </div>
        </div>

        {profile.isPending ? (
          <FormSkeleton rows={4} bare />
        ) : profile.data ? (
          <DetailsForm
            profile={profile.data}
            saving={save.isPending}
            failed={save.isError}
            onSave={(patch) => save.mutate(patch)}
          />
        ) : (
          <p className="text-sm text-destructive" role="alert">
            {t('settings.profile.details.loadError')}
          </p>
        )}
      </div>

      {/* Email addresses card */}
      <EmailAddresses />

      {/* Language card */}
      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <Globe className="h-5 w-5" weight="bold" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.profile.language.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.profile.language.body')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
          {languages.map((lang) => {
            const active = lang.code === currentLang;
            const FlagIcon = FLAG_COMPONENTS[lang.code];
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => pickLanguage(lang.code)}
                aria-pressed={active}
                className={cn(
                  'flex flex-col gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-muted/30 text-muted-foreground hover:border-foreground/30 hover:bg-muted hover:text-foreground',
                )}
              >
                {FlagIcon && <FlagIcon className="w-8 rounded-sm shadow-sm" />}
                <span className="flex flex-col gap-0.5">
                  <span className="font-heading text-sm font-semibold leading-tight">
                    {lang.nativeName}
                  </span>
                  <span className="text-xs">{lang.englishName}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Appearance card */}
      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            {theme === 'dark' ? (
              <Moon className="h-5 w-5" weight="bold" />
            ) : (
              <Sun className="h-5 w-5" weight="bold" />
            )}
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.profile.appearance.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.profile.appearance.body')}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <ThemeOption
            active={theme === 'light'}
            label={t('settings.profile.appearance.light')}
            icon={<Sun className="h-5 w-5" weight="bold" />}
            onClick={() => setTheme('light')}
            preview="light"
          />
          <ThemeOption
            active={theme === 'dark'}
            label={t('settings.profile.appearance.dark')}
            icon={<Moon className="h-5 w-5" weight="bold" />}
            onClick={() => setTheme('dark')}
            preview="dark"
          />
        </div>
      </div>
    </div>
  );
}

// DetailsForm edits the caller's own core.user fields. Local state is seeded
// from the server row and re-seeded whenever it changes, so a save elsewhere on
// the page (the language picker writes the same row) doesn't strand stale input.
function DetailsForm({
  profile,
  saving,
  failed,
  onSave,
}: {
  profile: AccountProfile;
  saving: boolean;
  failed: boolean;
  onSave: (patch: Partial<ReturnType<typeof toUpsert>>) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const [firstName, setFirstName] = useState(profile.first_name);
  const [lastName, setLastName] = useState(profile.last_name);
  const [phone, setPhone] = useState(profile.phone);
  const [birthDate, setBirthDate] = useState(profile.birth_date ?? '');
  const [postalCode, setPostalCode] = useState(profile.postal_code);
  const [city, setCity] = useState(profile.city);

  // Re-seed from the server row when the VALUES change (a save landed), not
  // when the object identity changes. The language picker writes the same
  // core.user row and pushes a fresh object into the cache; depending on
  // identity here would wipe whatever the user had typed but not yet saved.
  const {
    first_name: srvFirst,
    last_name: srvLast,
    phone: srvPhone,
    birth_date: srvBirth,
    postal_code: srvPostal,
    city: srvCity,
  } = profile;
  useEffect(() => {
    setFirstName(srvFirst);
    setLastName(srvLast);
    setPhone(srvPhone);
    setBirthDate(srvBirth ?? '');
    setPostalCode(srvPostal);
    setCity(srvCity);
  }, [srvFirst, srvLast, srvPhone, srvBirth, srvPostal, srvCity]);

  const dirty =
    firstName !== profile.first_name ||
    lastName !== profile.last_name ||
    phone !== profile.phone ||
    birthDate !== (profile.birth_date ?? '') ||
    postalCode !== profile.postal_code ||
    city !== profile.city;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        onSave({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          birth_date: birthDate.trim() === '' ? null : birthDate.trim(),
          postal_code: postalCode.trim(),
          city: city.trim(),
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('settings.profile.details.firstName')}>
          <Input
            value={firstName}
            onChange={(ev) => setFirstName(ev.target.value)}
            autoComplete="given-name"
            maxLength={120}
          />
        </Field>
        <Field label={t('settings.profile.details.lastName')}>
          <Input
            value={lastName}
            onChange={(ev) => setLastName(ev.target.value)}
            autoComplete="family-name"
            maxLength={120}
          />
        </Field>
        <Field label={t('settings.profile.details.phone')}>
          <Input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(ev) => setPhone(ev.target.value)}
            autoComplete="tel"
            maxLength={64}
          />
        </Field>
        <Field label={t('settings.profile.details.birthDate')}>
          {profile.birth_date_derived ? (
            // Derived from the stored national ID, which encodes it. Editable
            // here would mean two sources for one fact, and they drift — a
            // hand-edited birth date can make the identity linker refuse a
            // correct club record. Read-only, with the reason stated.
            <>
              <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 font-heading text-sm font-semibold tabular-nums text-foreground">
                {birthDate
                  ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                      new Date(birthDate),
                    )
                  : '—'}
              </p>
              <span className="text-xs text-muted-foreground">
                {t('settings.profile.details.birthDateFromId')}
              </span>
            </>
          ) : (
            /* A birth date is "click → pick", and the Calendar's year-grid mode
               avoids stepping back 35 years a month at a time. Capped at today —
               the server rejects a future birth date anyway. */
            <DatePickerPopover
              value={birthDate}
              onChange={setBirthDate}
              locale={i18n.language}
              max={todayISO()}
              placeholder={t('settings.profile.details.birthDatePlaceholder')}
              clearable
              clearLabel={t('settings.profile.details.clearDate')}
            />
          )}
        </Field>
        <Field label={t('settings.profile.details.postalCode')}>
          <Input
            value={postalCode}
            onChange={(ev) => setPostalCode(ev.target.value)}
            autoComplete="postal-code"
            maxLength={64}
          />
        </Field>
        <Field label={t('settings.profile.details.city')}>
          <Input
            value={city}
            onChange={(ev) => setCity(ev.target.value)}
            autoComplete="address-level2"
            maxLength={64}
          />
        </Field>
      </div>

      <p className="text-xs text-muted-foreground">
        {t('settings.profile.details.propagationNote')}
      </p>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving || !dirty}>
          {saving ? t('settings.profile.details.saving') : t('settings.profile.details.save')}
        </Button>
        {failed ? (
          <span className="text-xs text-destructive" role="alert">
            {t('settings.profile.details.saveError')}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-heading text-sm font-semibold text-foreground/80">{label}</span>
      {children}
    </label>
  );
}

function ThemeOption({
  active,
  label,
  icon,
  onClick,
  preview,
}: {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  preview: 'light' | 'dark';
}) {
  const isDark = preview === 'dark';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex w-32 flex-col overflow-hidden rounded-xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-primary' : 'border-border hover:border-foreground/30',
      )}
    >
      {/* Mini UI preview */}
      <div
        className={cn(
          'flex h-16 w-full flex-col gap-1 p-2',
          isDark ? 'bg-zinc-900' : 'bg-white',
        )}
      >
        <div
          className={cn(
            'h-2 w-3/4 rounded-full',
            isDark ? 'bg-zinc-700' : 'bg-zinc-200',
          )}
        />
        <div
          className={cn(
            'h-2 w-1/2 rounded-full',
            isDark ? 'bg-zinc-700' : 'bg-zinc-200',
          )}
        />
        <div className="mt-1 h-5 w-10 rounded-full bg-lime-400" />
      </div>
      {/* Label row */}
      <div
        className={cn(
          'flex items-center gap-1.5 px-3 py-2 font-heading text-sm font-semibold',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {icon}
        {label}
      </div>
    </button>
  );
}
